<script setup lang="ts">
import { computed } from 'vue';
import type { ChairListItemDto } from '../../types/chair';
import { chairPositionLabel } from '../../services/chairLabel';
import { useChairs } from '../../composables/useChairs';

/**
 * Zobrazení organizační pozice (Chair) — jediné místo, které rozhoduje, jak se pozice ukazuje (docs/tasks/059.md).
 * Zadává se buď celým `item` (ChairListItemDto), nebo jen `chairId` (dohledá se přes useChairs()).
 *
 * `variant`:
 *  - `full`     — „Jméno — Pozice“ (výchozí)
 *  - `compact`  — jen jméno uživatele, pozice a jednotka v tooltipu (šetří místo)
 *  - `user`     — jen jméno uživatele
 *  - `position` — jen název pozice (např. „Ředitel (Firma)“)
 *  - `stacked`  — jméno tučně a pod ním pozice a jednotka menším písmem (kde je místa dost)
 */
const props = withDefaults(defineProps<{
  item?: ChairListItemDto | null;
  chairId?: number | null;
  variant?: 'full' | 'compact' | 'user' | 'position' | 'stacked';
}>(), { variant: 'full' });

const { chairs } = useChairs();
const resolved = computed<ChairListItemDto | null>(() => {
  if (props.item) return props.item;
  if (props.chairId == null) return null;
  return chairs.value.find(c => c.chair.id === props.chairId) ?? null;
});
const position = computed(() => resolved.value ? chairPositionLabel(resolved.value) : '');
const tooltip = computed(() => resolved.value ? `${resolved.value.user.name} — ${position.value}` : '');
</script>

<template>
  <span v-if="!resolved" class="chair-label chair-label--unknown">{{ props.chairId != null ? `#${props.chairId}` : '—' }}</span>
  <span v-else-if="variant === 'compact'" class="chair-label" :title="tooltip">{{ resolved.user.name }}</span>
  <span v-else-if="variant === 'user'" class="chair-label">{{ resolved.user.name }}</span>
  <span v-else-if="variant === 'position'" class="chair-label">{{ position }}</span>
  <span v-else-if="variant === 'stacked'" class="chair-label chair-label--stacked">
    <strong>{{ resolved.user.name }}</strong>
    <small class="text-muted">{{ position }}</small>
  </span>
  <span v-else class="chair-label">{{ resolved.user.name }} — {{ position }}</span>
</template>

<style scoped>
.chair-label--stacked { display: inline-flex; flex-direction: column; line-height: 1.2; }
.chair-label--unknown { color: var(--bs-secondary-color); }
</style>
